// ============================================================================
// LMMS Shared Helpers
// Reusable utilities for status badges, formatting, etc.
// ============================================================================

/** Status badge style mapping for inline styles */
export const getStatusBadgeStyle = (
  status: string
): React.CSSProperties => {
  const base: React.CSSProperties = {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap' as const,
  };

  switch (status?.toUpperCase()) {
    // Positive / active
    case 'ACTIVE':
    case 'APPROVED':
    case 'OPEN':
      return { ...base, background: '#dcfce7', color: '#166534' };

    // Warning / pending
    case 'PENDING_APPROVAL':
    case 'PARTIALLY_CLOSED':
      return { ...base, background: '#fef3c7', color: '#92400e' };

    // Info / returned
    case 'RETURNED':
      return { ...base, background: '#dbeafe', color: '#1e40af' };

    // Danger / rejected
    case 'REJECTED':
      return { ...base, background: '#fce4ec', color: '#b71c1c' };

    // Neutral / draft / inactive / closed
    case 'DRAFT':
    case 'INACTIVE':
    case 'CLOSED':
    case 'FULLY_CLOSED':
      return { ...base, background: '#f1f5f9', color: '#475569' };

    default:
      return { ...base, background: '#f1f5f9', color: '#475569' };
  }
};

/** Human-readable display name for status codes */
export const getStatusDisplayName = (status: string): string => {
  switch (status?.toUpperCase()) {
    case 'PENDING_APPROVAL':
      return 'Pending Approval';
    case 'PARTIALLY_CLOSED':
      return 'Partially Closed';
    case 'FULLY_CLOSED':
      return 'Fully Closed';
    default:
      return status
        ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
        : '';
  }
};

/** Format number as INR currency string with commas */
export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/** Format date string to dd/MM/yyyy */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '-';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};
